import React, { useState, useEffect } from 'react';
import { getAdminToken, setAdminToken, clearAdminToken } from '../utils/adminAuth';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AdminLoginPage = ({ onLogin }) => {
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/admin/login`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ password })
      });
      const data = await res.json();
      if (res.ok && data.success) { setAdminToken(data.token); onLogin(data.token); }
      else { setError(data.detail || 'Mot de passe incorrect'); }
    } catch { setError('Erreur de connexion au serveur'); }
    finally { setLoading(false); }
  };

  return (
    <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', backgroundColor: '#F1F5F9' }}>
      <div style={{ backgroundColor: 'white', padding: '40px', borderRadius: '12px', boxShadow: '0 4px 20px rgba(0,0,0,0.1)', width: '100%', maxWidth: '400px' }}>
        <h1 style={{ fontSize: '24px', fontWeight: '700', color: '#1E3A5F', marginBottom: '10px', textAlign: 'center' }}>Administration</h1>
        <p style={{ color: '#64748B', fontSize: '14px', textAlign: 'center', marginBottom: '30px' }}>Atlantique Spa Boutique</p>
        <form onSubmit={handleSubmit} autoComplete="on">
          <input type="text" name="username" autoComplete="username" value="admin" readOnly
            style={{ position: 'absolute', opacity: 0, height: 0, width: 0, overflow: 'hidden' }} tabIndex={-1} />
          <div style={{ marginBottom: '20px' }}>
            <label style={{ display: 'block', marginBottom: '8px', color: '#475569', fontSize: '14px', fontWeight: '500' }}>Mot de passe</label>
            <input type="password" name="password" autoComplete="current-password" value={password} onChange={(e) => setPassword(e.target.value)} data-testid="login-password-input"
              style={{ width: '100%', padding: '12px 16px', border: '1px solid #E2E8F0', borderRadius: '8px', fontSize: '16px', outline: 'none', boxSizing: 'border-box' }}
              placeholder="Entrez le mot de passe" autoFocus />
          </div>
          {error && <div data-testid="login-error" style={{ padding: '12px 16px', backgroundColor: '#FEE2E2', color: '#991B1B', borderRadius: '8px', fontSize: '14px', marginBottom: '15px' }}>{error}</div>}
          <button type="submit" disabled={loading} data-testid="login-submit-btn"
            style={{ width: '100%', padding: '14px', backgroundColor: loading ? '#94A3B8' : '#1E3A5F', color: 'white', border: 'none', borderRadius: '8px', fontSize: '16px', fontWeight: '600', cursor: loading ? 'not-allowed' : 'pointer' }}>
            {loading ? 'Connexion...' : 'Se connecter'}
          </button>
        </form>
      </div>
    </div>
  );
};

const AdminNav = ({ active }) => (
  <div style={{ display: 'flex', marginBottom: '24px', backgroundColor: 'white', borderRadius: '10px', overflow: 'hidden', boxShadow: '0 1px 4px rgba(0,0,0,0.06)' }}>
    {[
      { key: 'dashboard', label: 'Dashboard', href: '/admin/dashboard' },
      { key: 'commandes', label: 'Commandes', href: '/admin/commandes' },
      { key: 'clients', label: 'Clients', href: '/admin/clients' },
      { key: 'factures', label: 'Factures', href: '/admin/factures' }
    ].map((tab, i) => (
      <a key={tab.key} href={tab.href} data-testid={`nav-${tab.key}`}
        style={{
          flex: 1, padding: '14px 20px', textAlign: 'center', fontSize: '14px', fontWeight: '600',
          color: active === tab.key ? 'white' : '#475569',
          backgroundColor: active === tab.key ? '#1E3A5F' : 'white',
          textDecoration: 'none',
          borderRight: i < 3 ? '1px solid #E2E8F0' : 'none'
        }}>
        {tab.label}
      </a>
    ))}
  </div>
);

const formatPrice = (p) => new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR' }).format(p || 0);
const formatDate = (d) => {
  if (!d) return '-';
  return new Date(d).toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric' });
};

const AdminClientsPage = () => {
  const [token, setToken] = useState(null);
  const [isVerifying, setIsVerifying] = useState(true);
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [sortKey, setSortKey] = useState('total_depense');
  const [sortDir, setSortDir] = useState('desc');
  const [message, setMessage] = useState(null);

  useEffect(() => {
    const verify = async () => {
      const t = getAdminToken();
      if (!t) { setIsVerifying(false); return; }
      try {
        const res = await fetch(`${API_URL}/api/admin/verify`, { headers: { 'Authorization': `Bearer ${t}` } });
        const d = await res.json();
        if (d.valid) setToken(t); else clearAdminToken();
      } catch { clearAdminToken(); }
      setIsVerifying(false);
    };
    verify();
  }, []);

  useEffect(() => {
    if (token) fetchCustomers();
  }, [token]);

  const fetchCustomers = async () => {
    try {
      setLoading(true);
      const res = await fetch(`${API_URL}/api/payments/customers`, { headers: { 'Authorization': `Bearer ${token}` } });
      if (res.status === 401) { handleLogout(); return; }
      if (res.ok) setCustomers(await res.json());
    } catch (e) { console.error('Error:', e); }
    finally { setLoading(false); }
  };

  const handleSync = async () => {
    setSyncing(true); setMessage(null);
    try {
      const res = await fetch(`${API_URL}/api/payments/customers/sync`, { method: 'POST', headers: { 'Authorization': `Bearer ${token}` } });
      if (res.ok) {
        const d = await res.json();
        setMessage({ type: 'success', text: `${d.synced} clients synchronises depuis les commandes.` });
        fetchCustomers();
      } else { setMessage({ type: 'error', text: 'Erreur lors de la synchronisation.' }); }
    } catch { setMessage({ type: 'error', text: 'Erreur de connexion.' }); }
    finally { setSyncing(false); }
  };

  const handleExportCSV = async () => {
    try {
      const res = await fetch(`${API_URL}/api/payments/customers/export`, { headers: { 'Authorization': `Bearer ${token}` } });
      if (res.ok) {
        const blob = await res.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url; a.download = 'clients_atlantique_spa.csv';
        document.body.appendChild(a); a.click(); a.remove();
        window.URL.revokeObjectURL(url);
      }
    } catch { setMessage({ type: 'error', text: 'Erreur lors de l\'export CSV.' }); }
  };

  const handleLogin = (t) => setToken(t);
  const handleLogout = () => { clearAdminToken(); setToken(null); setCustomers([]); };

  const handleSort = (key) => {
    if (sortKey === key) setSortDir(sortDir === 'asc' ? 'desc' : 'asc');
    else { setSortKey(key); setSortDir('desc'); }
  };

  const filtered = customers.filter(c => {
    if (!searchTerm) return true;
    const t = searchTerm.toLowerCase();
    return (c.nom || '').toLowerCase().includes(t) || (c.prenom || '').toLowerCase().includes(t)
      || (c.email || '').toLowerCase().includes(t) || (c.telephone || '').toLowerCase().includes(t)
      || (c.ville || '').toLowerCase().includes(t);
  });

  const sorted = [...filtered].sort((a, b) => {
    let va = a[sortKey] ?? '';
    let vb = b[sortKey] ?? '';
    if (typeof va === 'number') return sortDir === 'asc' ? va - vb : vb - va;
    va = String(va).toLowerCase(); vb = String(vb).toLowerCase();
    return sortDir === 'asc' ? va.localeCompare(vb) : vb.localeCompare(va);
  });

  const SortHeader = ({ label, field }) => (
    <th style={{ ...thStyle, cursor: 'pointer', userSelect: 'none' }} onClick={() => handleSort(field)}>
      {label} {sortKey === field ? (sortDir === 'asc' ? ' ↑' : ' ↓') : ''}
    </th>
  );

  if (isVerifying) return <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', backgroundColor: '#F1F5F9' }}><p style={{ color: '#64748B' }}>Verification...</p></div>;
  if (!token) return <AdminLoginPage onLogin={handleLogin} />;

  return (
    <div style={{ minHeight: '100vh', backgroundColor: '#F1F5F9', padding: '20px' }}>
      <div style={{ maxWidth: '1200px', margin: '0 auto' }}>
        <AdminNav active="clients" />

        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
          <div>
            <h1 style={{ fontSize: '28px', fontWeight: '700', color: '#0F172A', margin: 0 }}>Clients</h1>
            <p style={{ color: '#64748B', fontSize: '14px', margin: '5px 0 0 0' }}>{customers.length} client{customers.length !== 1 ? 's' : ''} enregistre{customers.length !== 1 ? 's' : ''}</p>
          </div>
          <div style={{ display: 'flex', gap: '10px' }}>
            <button onClick={handleSync} disabled={syncing} data-testid="sync-customers-btn"
              style={{ padding: '10px 18px', backgroundColor: '#F59E0B', color: 'white', border: 'none', borderRadius: '8px', cursor: syncing ? 'not-allowed' : 'pointer', fontSize: '13px', fontWeight: '600' }}>
              {syncing ? 'Synchronisation...' : 'Synchroniser'}
            </button>
            <button onClick={handleExportCSV} data-testid="export-csv-btn"
              style={{ padding: '10px 18px', backgroundColor: '#10B981', color: 'white', border: 'none', borderRadius: '8px', cursor: 'pointer', fontSize: '13px', fontWeight: '600' }}>
              Exporter CSV
            </button>
            <button onClick={handleLogout} data-testid="logout-btn"
              style={{ padding: '10px 18px', backgroundColor: 'white', border: '1px solid #E2E8F0', borderRadius: '8px', cursor: 'pointer', fontSize: '13px', color: '#64748B' }}>
              Deconnexion
            </button>
          </div>
        </div>

        {message && (
          <div data-testid="admin-message" style={{
            padding: '12px 18px', borderRadius: '8px', marginBottom: '15px', fontSize: '14px',
            backgroundColor: message.type === 'success' ? '#D1FAE5' : '#FEE2E2',
            color: message.type === 'success' ? '#065F46' : '#991B1B'
          }}>{message.text}</div>
        )}

        <div style={{ marginBottom: '20px' }}>
          <input type="text" value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)}
            placeholder="Rechercher par nom, email, telephone ou ville..." data-testid="search-customers-input"
            style={{ width: '100%', padding: '12px 18px', border: '1px solid #E2E8F0', borderRadius: '8px', fontSize: '14px', outline: 'none', boxSizing: 'border-box', backgroundColor: 'white' }} />
        </div>

        <div style={{ backgroundColor: 'white', borderRadius: '12px', boxShadow: '0 2px 10px rgba(0,0,0,0.05)', overflow: 'hidden' }}>
          {loading ? (
            <div style={{ padding: '60px', textAlign: 'center', color: '#64748B' }}>Chargement des clients...</div>
          ) : sorted.length === 0 ? (
            <div style={{ padding: '60px', textAlign: 'center', color: '#64748B' }}>
              {customers.length === 0 ? 'Aucun client. Cliquez sur "Synchroniser" pour importer depuis les commandes.' : 'Aucun resultat pour cette recherche.'}
            </div>
          ) : (
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: '1000px' }} data-testid="customers-table">
                <thead>
                  <tr style={{ backgroundColor: '#F8FAFC' }}>
                    <SortHeader label="Nom" field="nom" />
                    <SortHeader label="Prenom" field="prenom" />
                    <SortHeader label="Email" field="email" />
                    <th style={thStyle}>Telephone</th>
                    <SortHeader label="Ville" field="ville" />
                    <SortHeader label="Commandes" field="nombre_commandes" />
                    <SortHeader label="Total depense" field="total_depense" />
                    <SortHeader label="1ere commande" field="date_premiere_commande" />
                    <SortHeader label="Derniere" field="date_derniere_commande" />
                  </tr>
                </thead>
                <tbody>
                  {sorted.map((c, i) => (
                    <tr key={c.id || i} style={{ borderBottom: '1px solid #E2E8F0' }} data-testid={`customer-row-${i}`}>
                      <td style={tdStyle}><span style={{ fontWeight: '500', color: '#0F172A' }}>{c.nom || '-'}</span></td>
                      <td style={tdStyle}><span style={{ color: '#0F172A' }}>{c.prenom || '-'}</span></td>
                      <td style={tdStyle}><span style={{ color: '#475569' }}>{c.email}</span></td>
                      <td style={tdStyle}><span style={{ color: '#475569' }}>{c.telephone || '-'}</span></td>
                      <td style={tdStyle}><span style={{ color: '#475569' }}>{c.ville || '-'}</span></td>
                      <td style={tdStyle}>
                        <span style={{ display: 'inline-block', padding: '3px 10px', backgroundColor: '#EFF6FF', color: '#1E40AF', borderRadius: '12px', fontSize: '13px', fontWeight: '600' }}>
                          {c.nombre_commandes || 0}
                        </span>
                      </td>
                      <td style={{ ...tdStyle, textAlign: 'right' }}><span style={{ fontWeight: '600', color: '#0F172A' }}>{formatPrice(c.total_depense)}</span></td>
                      <td style={{ ...tdStyle, color: '#64748B', fontSize: '13px' }}>{formatDate(c.date_premiere_commande)}</td>
                      <td style={{ ...tdStyle, color: '#64748B', fontSize: '13px' }}>{formatDate(c.date_derniere_commande)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

const thStyle = { padding: '14px 16px', textAlign: 'left', fontSize: '11px', fontWeight: '600', color: '#64748B', textTransform: 'uppercase' };
const tdStyle = { padding: '14px 16px', fontSize: '14px' };

export default AdminClientsPage;
